// ============================================================
// OpsCom — Enterprise Platform Permissions
// 7-Layer Architecture:
//   1. Professional Role     — clinical grade / operational job role
//   2. Department            — which service / division the person belongs to
//   3. Capabilities          — additive competencies that unlock modules
//   4. Authority Level       — operational command authority (Team Lead → Executive)
//   5. Functional Groups     — modular admin permission bundles
//   6. Operational Assignment— dynamic shift / incident access (runtime)
//   7. Security Clearance    — tier-based sensitive data access controls
//
// Access = Role base  +  Capabilities  +  Authority  +  Functional Groups  +  Legacy OpRoles
// ============================================================

// ── 1. PROFESSIONAL ROLE ─────────────────────────────────────────────────────
// What the person IS: professional qualification / job role.
// Drives: operational workflows, baseline UI, clinical scope, legal framework.
export type ProfessionalRole =
  // Clinical
  | "first_responder" | "eca" | "technician" | "nurse" | "paramedic"
  | "critical_care_paramedic" | "advanced_paramedic" | "doctor" | "consultant"
  // Operations / Dispatch
  | "dispatcher" | "incident_officer" | "control_room_operator"
  // Fire & Rescue
  | "firefighter" | "leading_firefighter" | "crew_manager"
  | "watch_manager" | "station_manager" | "group_manager"
  | "fire_control" | "rescue_operative"
  // Search & Rescue
  | "search_technician" | "rescue_technician" | "sar_team_leader"
  | "search_manager" | "rescue_manager" | "sar_coordinator"
  // Security Operations
  | "security_officer" | "security_supervisor" | "security_control"
  | "event_safety_officer" | "close_protection"
  // Support Services — driving is a Capability, not a role
  | "logistics_coordinator" | "fleet_support" | "make_ready"
  | "welfare_officer" | "communications_operator" | "training_officer"
  // Corporate
  | "hr" | "finance" | "administrator"
  // Leadership
  | "manager" | "director";

// Backward-compatibility alias — all existing code using AppRole continues to work
export type AppRole = ProfessionalRole;

// ── 2. DEPARTMENT ────────────────────────────────────────────────────────────
// Which service / division the user primarily belongs to.
export type Department =
  | "operations" | "clinical" | "fire_rescue" | "search_rescue"
  | "security_operations" | "fleet" | "logistics" | "governance"
  | "technology" | "customer_services" | "corporate";

// ── 3. CAPABILITIES / COMPETENCIES ──────────────────────────────────────────
// Additive qualifications — dynamically unlock modules, workflows, equipment.
// A paramedic with emergency_driver can access vehicle sign-on; no role change needed.
export type Capability =
  | "emergency_driver" | "blue_light_driver" | "fleet_inspector"
  | "drug_competent" | "pgd_approved" | "prf_reviewer"
  | "bronze_commander" | "silver_commander" | "gold_commander"
  | "rope_rescue" | "water_rescue" | "swift_water" | "confined_space"
  | "ba_wearer" | "mountain_rescue" | "lowland_search"
  | "search_planning" | "search_management"
  | "drone_operator" | "k9_handler" | "instructor" | "assessor"
  | "welfare_officer_cap" | "gis_mapping" | "rescue_boat_operator"
  | "technical_rescue";

// ── 4. AUTHORITY LEVEL ───────────────────────────────────────────────────────
// Operational command authority — separate from professional role.
// Controls: command dashboards, incident escalation, tactical/strategic permissions.
// Does NOT automatically grant HR / payroll / governance / PRF review access.
export type AuthorityLevel =
  | "none" | "team_leader" | "supervisor" | "duty_manager"
  | "bronze_commander" | "silver_commander" | "gold_commander" | "executive";

// ── 5. FUNCTIONAL GROUPS ────────────────────────────────────────────────────
// Modular permission bundles — a user can safely hold multiple groups.
// IT/platform admins do NOT automatically gain clinical, governance, or payroll access.
// Operational access and platform administration remain separated.
export type FunctionalGroup =
  | "governance" | "compliance" | "it_support" | "fleet_admin"
  | "training_admin" | "hr_admin" | "finance_admin" | "customer_services_admin"
  | "radio_admin" | "platform_admin" | "gis_admin" | "communications_admin";

// ── LEGACY: Operational / organisational roles ───────────────────────────────
// Held IN ADDITION to professional role. Kept for backward compat with existing DB.
// New systems should use AuthorityLevel + FunctionalGroup instead.
export type OperationalRole =
  | "fire_crew" | "fire_oic" | "rescue_team" | "make_ready_crew"
  | "governance" | "compliance" | "management" | "director"
  | "management_support";

export const OPERATIONAL_ROLE_LABELS: Record<OperationalRole, string> = {
  fire_crew: "Fire Crew",
  fire_oic: "Fire OIC",
  rescue_team: "Rescue Team",
  make_ready_crew: "Make Ready Crew",
  governance: "Governance Lead",
  compliance: "Compliance Officer",
  management: "Operations Management",
  director: "Director",
  management_support: "Management Support",
};

export const OPERATIONAL_ROLE_COLORS: Record<OperationalRole, string> = {
  fire_crew:          "bg-red-100 text-red-800 border-red-200",
  fire_oic:           "bg-red-200 text-red-900 border-red-400",
  rescue_team:        "bg-orange-100 text-orange-800 border-orange-200",
  make_ready_crew:    "bg-amber-100 text-amber-800 border-amber-200",
  governance:         "bg-indigo-100 text-indigo-800 border-indigo-300",
  compliance:         "bg-blue-100 text-blue-800 border-blue-300",
  management:         "bg-purple-100 text-purple-800 border-purple-300",
  director:           "bg-purple-200 text-purple-900 border-purple-400",
  management_support: "bg-slate-100 text-slate-700 border-slate-300",
};

export const OPERATIONAL_ROLE_SHORT: Record<OperationalRole, string> = {
  fire_crew: "FIRE", fire_oic: "OIC", rescue_team: "RESC",
  make_ready_crew: "MRC", governance: "GOV", compliance: "COMP",
  management: "MGMT", director: "DIR", management_support: "MSUP",
};

// ─── Nav keys ─────────────────────────────────────────────────────────────────
export type NavKey =
  | "dashboard" | "cad" | "reports" | "mdt"
  | "major_incident" | "crew_status" | "briefing"
  | "handover" | "posts" | "patient_lookup"
  | "events"
  | "shifts_available" | "shifts_my" | "shifts_applications" | "calendar"
  | "fleet_vehicles" | "fleet_cleaning" | "fleet_defects" | "fleet_make_ready" | "fleet_daily_inspection"
  | "fleet_dashboard" | "fleet_servicing" | "fleet_drivers" | "fleet_incidents" | "fleet_fuel" | "fleet_handovers" | "fleet_audits"
  | "fleet_sign_on" | "fleet_mileage" | "fleet_compliance" | "fleet_workshop" | "fleet_recalls"
  | "logistics_equipment" | "logistics_stores" | "logistics_ppe" | "logistics_cd_register" | "logistics_loler" | "logistics_suppliers"
  | "cd_register" | "medical_gases"
  | "staff" | "customers" | "applications"
  | "people_dashboard" | "people_absence" | "people_onboarding" | "people_appraisals" | "people_hr_docs" | "people_wellbeing" | "people_on_call"
  | "medicine" | "pgd" | "policies" | "sops" | "cqc" | "vault"
  | "gov_dashboard" | "gov_incidents" | "gov_complaints" | "gov_risk" | "gov_audit" | "gov_learning"
  | "gov_actions" | "gov_dspt" | "gov_assurance" | "gov_serious_incidents"
  | "director_home" | "director_command" | "director_risk" | "director_assurance" | "director_timeline" | "manager_home" | "compliance_home"
  | "prf_review" | "stats" | "training" | "training_matrix"
  | "training_cpd" | "training_induction" | "training_plans" | "training_competency"
  | "vault_acks" | "forms_library" | "documentation_hub" | "doc_register"
  | "payroll" | "kit_allocation" | "my_pay" | "settings" | "permissions"
  | "admin_accounts"
  // ── Fire & Rescue ──────────────────────────────────────────────────────────
  | "fire_appliance" | "fire_mobilisation" | "fire_resource_tracking"
  // ── Search & Rescue ───────────────────────────────────────────────────────
  | "sar_planning" | "sar_mapping" | "sar_tracking" | "sar_sectors"
  | "sar_log" | "sar_equipment"
  // ── Security Operations ───────────────────────────────────────────────────
  | "security_dashboard" | "security_patrols" | "security_incidents"
  | "security_access" | "security_missing_persons";

// ─── Base access sets (private helpers) ───────────────────────────────────────
const CLINICAL_BASE: NavKey[] = [
  "dashboard", "cad", "reports", "mdt",
  "major_incident", "crew_status", "briefing",
  "handover", "posts", "patient_lookup",
  "events", "shifts_available", "shifts_my", "calendar",
  "medicine", "pgd", "policies", "sops", "cqc", "vault",
  "fleet_sign_on", "fleet_daily_inspection", "fleet_defects", "fleet_handovers",
  "training", "training_cpd", "training_competency", "my_pay", "settings", "forms_library", "documentation_hub",
];

const FIRE_BASE: NavKey[] = [
  "dashboard", "mdt", "crew_status", "briefing",
  "fire_appliance", "fleet_sign_on", "fleet_daily_inspection", "fleet_defects", "fleet_handovers",
  "shifts_available", "shifts_my", "calendar",
  "training", "training_cpd", "training_competency", "my_pay", "settings",
  "documentation_hub", "policies", "sops", "vault", "forms_library",
];

const SAR_BASE: NavKey[] = [
  "dashboard", "mdt", "crew_status",
  "sar_log", "sar_equipment",
  "shifts_available", "shifts_my", "calendar",
  "training", "training_cpd", "training_competency", "my_pay", "settings",
  "documentation_hub", "policies", "sops", "vault",
];

const SECURITY_BASE: NavKey[] = [
  "dashboard", "security_dashboard", "security_patrols", "security_incidents",
  "shifts_available", "shifts_my", "calendar",
  "training", "training_cpd", "my_pay", "settings",
  "documentation_hub",
];

const ALL_ACCESS: NavKey[] = [
  "dashboard", "cad", "reports", "mdt",
  "major_incident", "crew_status", "briefing",
  "handover", "posts", "patient_lookup",
  "events", "shifts_available", "shifts_my", "shifts_applications", "calendar",
  "fleet_vehicles", "fleet_cleaning", "fleet_defects", "fleet_make_ready", "fleet_daily_inspection",
  "fleet_dashboard", "fleet_servicing", "fleet_drivers", "fleet_incidents", "fleet_fuel", "fleet_handovers", "fleet_audits",
  "fleet_sign_on", "fleet_mileage", "fleet_compliance", "fleet_workshop", "fleet_recalls",
  "logistics_equipment", "logistics_stores", "logistics_ppe", "logistics_cd_register", "logistics_loler", "logistics_suppliers",
  "cd_register", "medical_gases",
  "staff", "customers", "applications",
  "people_dashboard", "people_absence", "people_onboarding", "people_appraisals", "people_hr_docs", "people_wellbeing", "people_on_call",
  "medicine", "pgd", "policies", "sops", "cqc", "vault",
  "gov_dashboard", "gov_incidents", "gov_complaints", "gov_risk", "gov_audit", "gov_learning", "gov_serious_incidents",
  "gov_actions", "gov_dspt", "gov_assurance",
  "director_home", "director_command", "director_risk", "director_assurance", "director_timeline", "manager_home", "compliance_home",
  "prf_review", "stats", "training", "training_matrix",
  "training_cpd", "training_induction", "training_plans", "training_competency",
  "vault_acks", "forms_library", "documentation_hub", "doc_register",
  "payroll", "kit_allocation", "my_pay", "settings", "permissions", "admin_accounts",
  // Fire & Rescue
  "fire_appliance", "fire_mobilisation", "fire_resource_tracking",
  // Search & Rescue
  "sar_planning", "sar_mapping", "sar_tracking", "sar_sectors", "sar_log", "sar_equipment",
  // Security Operations
  "security_dashboard", "security_patrols", "security_incidents", "security_access", "security_missing_persons",
];

// ─── Primary role permissions ─────────────────────────────────────────────────
export const ROLE_PERMISSIONS: Record<ProfessionalRole, NavKey[]> = {
  // ── Leadership ────────────────────────────────────────────────────────────
  director: ALL_ACCESS,
  manager:  ALL_ACCESS,

  // ── Clinical ─────────────────────────────────────────────────────────────
  first_responder: [
    "dashboard", "reports", "crew_status", "briefing",
    "shifts_available", "shifts_my", "calendar",
    "policies", "sops", "vault", "training", "training_cpd", "training_competency",
    "my_pay", "settings", "forms_library", "documentation_hub",
  ],
  eca: [
    "dashboard", "reports", "crew_status", "briefing",
    "shifts_available", "shifts_my", "calendar",
    "policies", "sops", "vault", "training", "training_cpd", "training_competency",
    "my_pay", "settings", "forms_library", "documentation_hub",
  ],
  technician: [
    "dashboard", "cad", "reports", "mdt",
    "major_incident", "crew_status", "briefing",
    "handover", "posts", "patient_lookup",
    "events", "shifts_available", "shifts_my", "calendar",
    "medicine", "policies", "sops", "cqc", "vault",
    "fleet_sign_on", "fleet_daily_inspection", "fleet_defects", "fleet_handovers",
    "training", "training_cpd", "training_competency", "my_pay", "settings", "forms_library", "documentation_hub",
  ],
  nurse:                  [...CLINICAL_BASE],
  paramedic:              [...CLINICAL_BASE],
  critical_care_paramedic:[...CLINICAL_BASE],
  advanced_paramedic:     [...CLINICAL_BASE],
  doctor:                 [...CLINICAL_BASE],
  consultant:             [...CLINICAL_BASE],

  // ── Operations / Dispatch ─────────────────────────────────────────────────
  dispatcher: [
    "dashboard", "cad", "mdt",
    "crew_status", "briefing", "patient_lookup", "major_incident",
    "events", "shifts_available", "calendar",
    "fleet_vehicles", "fleet_defects",
    "training", "my_pay", "settings",
  ],
  incident_officer: [
    "dashboard", "cad", "mdt",
    "crew_status", "briefing", "patient_lookup", "major_incident", "director_command",
    "events", "shifts_available", "calendar",
    "fleet_vehicles", "fleet_defects",
    "training", "my_pay", "settings",
  ],
  control_room_operator: [
    "dashboard", "cad", "mdt",
    "crew_status", "briefing", "patient_lookup", "major_incident",
    "events", "shifts_available", "calendar",
    "fleet_vehicles", "fleet_defects",
    "training", "my_pay", "settings",
  ],

  // ── Fire & Rescue ─────────────────────────────────────────────────────────
  firefighter:       [...FIRE_BASE],
  leading_firefighter:[...FIRE_BASE],
  crew_manager:      [...FIRE_BASE, "fire_mobilisation"],
  watch_manager:     [...FIRE_BASE, "fire_mobilisation", "fire_resource_tracking", "fleet_vehicles", "shifts_applications"],
  station_manager:   [...FIRE_BASE, "fire_mobilisation", "fire_resource_tracking", "fleet_vehicles", "fleet_cleaning", "shifts_applications", "staff", "people_dashboard"],
  group_manager: [
    ...FIRE_BASE, "fire_mobilisation", "fire_resource_tracking",
    "fleet_vehicles", "fleet_cleaning", "fleet_servicing", "fleet_dashboard",
    "shifts_applications", "staff", "people_dashboard", "people_absence",
    "reports", "stats", "cad",
  ],
  fire_control: [
    "dashboard", "cad", "mdt", "crew_status", "briefing",
    "fire_mobilisation", "fire_resource_tracking",
    "major_incident", "fleet_vehicles", "fleet_defects",
    "events", "shifts_available", "calendar",
    "training", "my_pay", "settings",
  ],
  rescue_operative: [...FIRE_BASE, "sar_equipment"],

  // ── Search & Rescue ───────────────────────────────────────────────────────
  search_technician: [...SAR_BASE],
  rescue_technician: [...SAR_BASE, "sar_tracking"],
  sar_team_leader:   [...SAR_BASE, "sar_tracking", "sar_sectors", "sar_planning"],
  search_manager:    [...SAR_BASE, "sar_tracking", "sar_sectors", "sar_planning", "sar_mapping"],
  rescue_manager:    [...SAR_BASE, "sar_tracking", "sar_sectors", "sar_planning", "sar_mapping"],
  sar_coordinator: [
    "dashboard", "cad", "mdt", "crew_status", "major_incident",
    "sar_planning", "sar_mapping", "sar_tracking", "sar_sectors", "sar_log", "sar_equipment",
    "briefing", "events", "shifts_available", "shifts_applications", "calendar",
    "training", "my_pay", "settings",
  ],

  // ── Security Operations ───────────────────────────────────────────────────
  security_officer:   [...SECURITY_BASE],
  security_supervisor:[...SECURITY_BASE, "security_access", "shifts_applications"],
  security_control: [
    "dashboard", "security_dashboard", "security_patrols", "security_incidents",
    "security_access", "security_missing_persons",
    "crew_status", "major_incident",
    "events", "shifts_available", "calendar",
    "training", "my_pay", "settings",
  ],
  event_safety_officer: [
    "dashboard", "security_dashboard", "security_incidents",
    "events", "shifts_available", "shifts_my", "calendar",
    "training", "training_cpd", "my_pay", "settings",
  ],
  close_protection: [...SECURITY_BASE],

  // ── Support Services ──────────────────────────────────────────────────────
  logistics_coordinator: [
    "dashboard",
    "logistics_equipment", "logistics_stores", "logistics_ppe",
    "logistics_cd_register", "logistics_loler", "logistics_suppliers",
    "fleet_sign_on", "shifts_available", "shifts_my", "calendar",
    "training", "my_pay", "settings",
  ],
  fleet_support: [
    "dashboard",
    "fleet_sign_on", "fleet_daily_inspection", "fleet_cleaning",
    "fleet_defects", "fleet_handovers", "fleet_make_ready", "fleet_mileage", "fleet_fuel",
    "shifts_available", "shifts_my", "calendar",
    "training", "my_pay", "settings",
  ],
  make_ready: [
    "dashboard",
    "fleet_make_ready", "fleet_daily_inspection", "fleet_cleaning", "fleet_defects",
    "shifts_available", "shifts_my", "calendar",
    "training", "my_pay", "settings",
  ],
  welfare_officer: [
    "dashboard", "staff", "people_wellbeing", "people_on_call",
    "shifts_available", "shifts_my", "calendar",
    "training", "my_pay", "settings",
  ],
  communications_operator: [
    "dashboard", "cad", "crew_status", "briefing",
    "shifts_available", "shifts_my", "calendar",
    "training", "my_pay", "settings",
  ],
  training_officer: [
    "dashboard", "training", "training_matrix", "training_cpd",
    "training_induction", "training_plans", "training_competency",
    "staff", "people_dashboard",
    "shifts_available", "shifts_my", "calendar",
    "documentation_hub", "forms_library",
    "my_pay", "settings",
  ],

  // ── Corporate ─────────────────────────────────────────────────────────────
  hr: [
    "dashboard", "staff", "people_dashboard", "people_absence",
    "people_onboarding", "people_appraisals", "people_hr_docs",
    "people_wellbeing", "people_on_call", "applications",
    "payroll", "my_pay",
    "training_matrix", "training_induction", "training_plans", "training_competency",
    "settings",
  ],
  finance: [
    "dashboard", "payroll", "my_pay", "settings",
  ],
  administrator: [
    "dashboard", "staff", "settings", "training",
    "my_pay", "calendar", "documentation_hub", "forms_library",
  ],
};

// ─── Capability nav grants ────────────────────────────────────────────────────
// Unlocked when a user holds a specific competency/qualification.
export const CAPABILITY_NAV_GRANTS: Partial<Record<Capability, NavKey[]>> = {
  emergency_driver:  ["fleet_sign_on", "fleet_daily_inspection", "fleet_defects", "fleet_handovers", "fleet_mileage"],
  blue_light_driver: ["fleet_sign_on", "fleet_daily_inspection", "fleet_defects", "fleet_handovers", "fleet_mileage", "fleet_fuel"],
  fleet_inspector:   ["fleet_vehicles", "fleet_compliance", "fleet_audits", "fleet_servicing", "fleet_recalls"],
  drug_competent:    ["medicine", "cd_register", "logistics_cd_register"],
  pgd_approved:      ["pgd"],
  prf_reviewer:      ["prf_review"],
  bronze_commander:  ["major_incident"],
  silver_commander:  ["major_incident", "director_command"],
  gold_commander:    ["major_incident", "director_command", "director_risk", "director_assurance"],
  search_planning:   ["sar_planning", "sar_sectors", "sar_mapping"],
  gis_mapping:       ["sar_mapping"],
  drone_operator:    ["sar_tracking"],
  instructor:        ["training_matrix", "training_plans", "training_competency"],
  assessor:          ["training_competency", "training_matrix"],
};

// ─── Authority level nav grants ───────────────────────────────────────────────
// Unlocked based on operational command authority.
// Does NOT grant HR, payroll, governance, or sensitive admin access.
export const AUTHORITY_NAV_GRANTS: Partial<Record<AuthorityLevel, NavKey[]>> = {
  team_leader:      ["shifts_applications"],
  supervisor:       ["shifts_applications", "fleet_audits", "stats"],
  duty_manager:     [
    "staff", "people_dashboard", "shifts_applications",
    "fleet_vehicles", "fleet_dashboard", "fleet_mileage", "fleet_compliance", "stats", "manager_home",
  ],
  bronze_commander: ["major_incident", "director_command"],
  silver_commander: ["major_incident", "director_command", "director_risk"],
  gold_commander:   ["major_incident", "director_command", "director_risk", "director_assurance"],
  executive:        [
    "major_incident", "director_home", "director_command", "director_risk",
    "director_assurance", "director_timeline", "manager_home", "compliance_home",
    "stats", "prf_review", "permissions", "admin_accounts",
  ],
};

// ─── Functional group nav grants ──────────────────────────────────────────────
// Modular permission bundles. Users can hold multiple groups.
// Platform/IT admin does NOT grant clinical, governance, or payroll access.
export const FUNCTIONAL_GROUP_NAV_GRANTS: Partial<Record<FunctionalGroup, NavKey[]>> = {
  governance: [
    "prf_review", "stats", "staff", "permissions", "applications", "fleet_audits",
    "fleet_incidents", "cd_register", "people_hr_docs", "people_wellbeing",
    "gov_dashboard", "gov_incidents", "gov_complaints", "gov_risk", "gov_audit",
    "gov_learning", "gov_actions", "gov_dspt", "gov_assurance", "gov_serious_incidents",
    "compliance_home", "training_matrix", "training_cpd", "training_induction",
    "training_plans", "training_competency", "vault_acks", "documentation_hub", "doc_register",
  ],
  compliance: [
    "cqc", "stats", "permissions", "fleet_audits", "fleet_incidents", "cd_register",
    "medical_gases", "people_hr_docs", "gov_dashboard", "gov_incidents", "gov_risk",
    "gov_audit", "gov_learning", "gov_actions", "gov_dspt", "gov_assurance", "compliance_home",
    "training_matrix", "training_cpd", "training_induction", "training_plans",
    "training_competency", "vault_acks", "documentation_hub", "doc_register",
  ],
  hr_admin: [
    "staff", "customers", "applications", "payroll", "kit_allocation", "stats",
    "shifts_applications", "people_dashboard", "people_absence", "people_onboarding",
    "people_appraisals", "people_hr_docs", "people_wellbeing", "people_on_call",
    "training_matrix", "training_plans", "training_induction", "training_competency",
    "manager_home", "documentation_hub", "doc_register",
  ],
  fleet_admin: [
    "fleet_vehicles", "fleet_cleaning", "fleet_defects", "fleet_make_ready",
    "fleet_daily_inspection", "fleet_dashboard", "fleet_servicing", "fleet_drivers",
    "fleet_incidents", "fleet_fuel", "fleet_handovers", "fleet_audits",
    "fleet_sign_on", "fleet_mileage", "fleet_compliance", "fleet_workshop", "fleet_recalls",
    "logistics_equipment", "logistics_stores", "logistics_ppe", "logistics_cd_register",
    "logistics_loler", "logistics_suppliers", "cd_register", "medical_gases", "kit_allocation",
  ],
  training_admin: [
    "training", "training_matrix", "training_cpd", "training_induction",
    "training_plans", "training_competency", "staff", "people_dashboard",
    "documentation_hub", "forms_library",
  ],
  finance_admin:           ["payroll", "my_pay"],
  platform_admin:          ["admin_accounts", "settings", "permissions"],
  it_support:              ["admin_accounts", "settings"],
  customer_services_admin: ["customers", "applications"],
  gis_admin:               ["sar_mapping", "sar_planning"],
  communications_admin:    [],
  radio_admin:             [],
};

// ─── Legacy operational role bonus nav keys ───────────────────────────────────
// Extra nav access from legacy operational role "hats". Kept for DB backward compat.
// Prefer FunctionalGroup + AuthorityLevel for new assignments.
export const OP_ROLE_NAV_GRANTS: Partial<Record<OperationalRole, NavKey[]>> = {
  governance: ["prf_review", "stats", "staff", "permissions", "applications", "fleet_audits", "fleet_incidents", "cd_register", "people_hr_docs", "people_wellbeing", "gov_dashboard", "gov_incidents", "gov_complaints", "gov_risk", "gov_audit", "gov_learning", "gov_actions", "gov_dspt", "gov_assurance", "gov_serious_incidents", "compliance_home", "training_matrix", "training_cpd", "training_induction", "training_plans", "training_competency", "vault_acks", "documentation_hub", "doc_register"],
  compliance: ["cqc", "stats", "permissions", "fleet_audits", "fleet_incidents", "cd_register", "medical_gases", "people_hr_docs", "gov_dashboard", "gov_incidents", "gov_risk", "gov_audit", "gov_learning", "gov_actions", "gov_dspt", "gov_assurance", "compliance_home", "training_matrix", "training_cpd", "training_induction", "training_plans", "training_competency", "vault_acks", "documentation_hub", "doc_register"],
  management: ["staff", "customers", "applications", "payroll", "kit_allocation", "stats", "permissions", "shifts_applications", "fleet_dashboard", "fleet_servicing", "fleet_drivers", "fleet_incidents", "fleet_fuel", "fleet_handovers", "fleet_audits", "fleet_sign_on", "fleet_mileage", "fleet_compliance", "fleet_workshop", "fleet_recalls", "logistics_equipment", "logistics_stores", "logistics_ppe", "logistics_cd_register", "logistics_loler", "logistics_suppliers", "cd_register", "medical_gases", "people_dashboard", "people_absence", "people_onboarding", "people_appraisals", "people_hr_docs", "people_wellbeing", "people_on_call", "people_on_call", "gov_dashboard", "gov_incidents", "gov_complaints", "gov_risk", "gov_audit", "gov_learning", "gov_actions", "gov_dspt", "manager_home", "training_matrix", "training_cpd", "training_induction", "training_plans", "training_competency", "vault_acks", "documentation_hub", "doc_register"],
  director:   ["staff", "customers", "applications", "payroll", "kit_allocation", "stats", "permissions", "prf_review", "shifts_applications", "fleet_dashboard", "fleet_servicing", "fleet_drivers", "fleet_incidents", "fleet_fuel", "fleet_handovers", "fleet_audits", "fleet_sign_on", "fleet_mileage", "fleet_compliance", "fleet_workshop", "fleet_recalls", "logistics_equipment", "logistics_stores", "logistics_ppe", "logistics_cd_register", "logistics_loler", "logistics_suppliers", "cd_register", "medical_gases", "people_dashboard", "people_absence", "people_onboarding", "people_appraisals", "people_hr_docs", "people_wellbeing", "people_on_call", "people_on_call", "gov_dashboard", "gov_incidents", "gov_complaints", "gov_risk", "gov_audit", "gov_learning", "gov_actions", "gov_dspt", "gov_assurance", "gov_serious_incidents", "director_home", "director_command", "director_risk", "director_assurance", "director_timeline", "manager_home", "compliance_home", "training_matrix", "training_cpd", "training_induction", "training_plans", "training_competency", "vault_acks", "admin_accounts", "documentation_hub", "doc_register"],
  fire_crew:  [],      // fire crew nav is same as their clinical base; shift filtering does the rest
  fire_oic:   [],
};

// ─── Access functions ─────────────────────────────────────────────────────────

/** Full 7-layer access check. Checks role, capabilities, authority, functional groups, and legacy op roles. */
export function canWithProfile(
  role: ProfessionalRole,
  opts: {
    capabilities?: Capability[];
    authorityLevel?: AuthorityLevel;
    functionalGroups?: FunctionalGroup[];
    operationalRoles?: OperationalRole[];
  },
  key: NavKey,
): boolean {
  if (ROLE_PERMISSIONS[role]?.includes(key)) return true;
  const caps = opts.capabilities ?? [];
  if (caps.some(c => CAPABILITY_NAV_GRANTS[c]?.includes(key))) return true;
  const level = opts.authorityLevel ?? "none";
  if (level !== "none" && AUTHORITY_NAV_GRANTS[level]?.includes(key)) return true;
  const groups = opts.functionalGroups ?? [];
  if (groups.some(g => FUNCTIONAL_GROUP_NAV_GRANTS[g]?.includes(key))) return true;
  const opRoles = opts.operationalRoles ?? [];
  if (opRoles.some(op => OP_ROLE_NAV_GRANTS[op]?.includes(key))) return true;
  return false;
}

/** Simple check using role only. */
export function can(role: AppRole, key: NavKey): boolean {
  return ROLE_PERMISSIONS[role]?.includes(key) ?? false;
}

/** Backward-compatible check using role + legacy operational roles. */
export function canWithOps(role: AppRole | null, opRoles: OperationalRole[], key: NavKey): boolean {
  if (!role) return false;
  return canWithProfile(role, { operationalRoles: opRoles }, key);
}

/** Returns true if the user has operational management access. */
export function isManagement(role: AppRole | null, opRoles: OperationalRole[]): boolean {
  if (!role) return false;
  return (
    role === "manager" || role === "director" ||
    opRoles.includes("management") || opRoles.includes("director")
  );
}

/** Returns true if the role is a clinical qualification. */
export function isClinicalRole(role: AppRole): boolean {
  return CLINICAL_ROLES_SET.has(role);
}

const CLINICAL_ROLES_SET = new Set<AppRole>([
  "first_responder", "eca", "technician", "nurse", "paramedic",
  "critical_care_paramedic", "advanced_paramedic", "doctor", "consultant",
]);

// ─── Clinical seniority level ─────────────────────────────────────────────────
// Used for shift eligibility: staff can only book shifts AT or BELOW their level.
export const CLINICAL_LEVEL: Partial<Record<AppRole, number>> = {
  first_responder: 1, eca: 2, technician: 3,
  paramedic: 4, critical_care_paramedic: 5,
  advanced_paramedic: 6, nurse: 4, doctor: 7, consultant: 8,
  manager: 9, director: 9, dispatcher: 0,
  // Non-clinical roles get 0 — clinical-level shift eligibility not applicable
  fleet_support: 0, make_ready: 0, logistics_coordinator: 0,
};

// Minimum clinical level required to book a shift of each role type
export const SHIFT_ROLE_MIN_LEVEL: Record<string, number> = {
  "First Responder": 1,
  "ECA":             2,
  "EMT":             2,
  "Technician":      3,
  "Paramedic":       4,
  "Nurse":           4,
  "Crit. Care Para.": 5,
  "Adv. Paramedic":  6,
  "Doctor":          7,
  "Consultant":      8,
  "Firefighter":     -10, // fire_crew department required
  "Fire OIC":        -11,
  "SAR Technician":  -20, // search_rescue department
  "Security Officer":-30, // security_operations department
};

// ─── Role labels / display ────────────────────────────────────────────────────
export const ROLE_LABELS: Record<AppRole, string> = {
  // Clinical
  first_responder: "First Responder", eca: "Emergency Care Assistant",
  technician: "Technician", nurse: "Nurse", paramedic: "Paramedic",
  critical_care_paramedic: "Critical Care Paramedic",
  advanced_paramedic: "Advanced Paramedic", doctor: "Doctor", consultant: "Consultant",
  // Operations
  dispatcher: "Dispatcher", incident_officer: "Incident Officer",
  control_room_operator: "Control Room Operator",
  // Fire & Rescue
  firefighter: "Firefighter", leading_firefighter: "Leading Firefighter",
  crew_manager: "Crew Manager", watch_manager: "Watch Manager",
  station_manager: "Station Manager", group_manager: "Group Manager",
  fire_control: "Fire Control", rescue_operative: "Rescue Operative",
  // Search & Rescue
  search_technician: "Search Technician", rescue_technician: "Rescue Technician",
  sar_team_leader: "Team Leader (SAR)", search_manager: "Search Manager",
  rescue_manager: "Rescue Manager", sar_coordinator: "SAR Coordinator",
  // Security
  security_officer: "Security Officer", security_supervisor: "Security Supervisor",
  security_control: "Security Control", event_safety_officer: "Event Safety Officer",
  close_protection: "Close Protection",
  // Support
  logistics_coordinator: "Logistics Coordinator", fleet_support: "Fleet Support",
  make_ready: "Make Ready", welfare_officer: "Welfare Officer",
  communications_operator: "Communications Operator", training_officer: "Training Officer",
  // Corporate
  hr: "HR", finance: "Finance", administrator: "Administrator",
  // Leadership
  manager: "Manager", director: "Director",
};

export const ROLE_SHORT: Record<AppRole, string> = {
  first_responder: "FR", eca: "ECA", technician: "TECH",
  nurse: "RN", paramedic: "PARA", critical_care_paramedic: "CCP",
  advanced_paramedic: "AP", doctor: "DR", consultant: "CONS",
  dispatcher: "DISP", incident_officer: "IO", control_room_operator: "CRO",
  firefighter: "FF", leading_firefighter: "LFF", crew_manager: "CM",
  watch_manager: "WM", station_manager: "SM", group_manager: "GM",
  fire_control: "FCC", rescue_operative: "RO",
  search_technician: "ST", rescue_technician: "RT", sar_team_leader: "TL",
  search_manager: "SrchMgr", rescue_manager: "RescMgr", sar_coordinator: "SARC",
  security_officer: "SO", security_supervisor: "SS", security_control: "SC",
  event_safety_officer: "ESO", close_protection: "CP",
  logistics_coordinator: "LOG", fleet_support: "FS", make_ready: "MR",
  welfare_officer: "WO", communications_operator: "COMMS", training_officer: "TO",
  hr: "HR", finance: "FIN", administrator: "ADMIN",
  manager: "MGR", director: "DIR",
};

// Dark sidebar badge colours
export const ROLE_COLORS: Record<AppRole, string> = {
  first_responder: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  eca: "bg-teal-500/20 text-teal-300 border-teal-500/30",
  technician: "bg-green-500/20 text-green-300 border-green-500/30",
  nurse: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  paramedic: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  critical_care_paramedic: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  advanced_paramedic: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  doctor: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  consultant: "bg-rose-500/20 text-rose-300 border-rose-500/30",
  dispatcher: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  incident_officer: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  control_room_operator: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  firefighter: "bg-red-600/20 text-red-300 border-red-500/30",
  leading_firefighter: "bg-red-600/20 text-red-300 border-red-600/30",
  crew_manager: "bg-red-700/20 text-red-200 border-red-600/30",
  watch_manager: "bg-red-700/20 text-red-200 border-red-700/30",
  station_manager: "bg-red-800/20 text-red-200 border-red-700/30",
  group_manager: "bg-red-900/20 text-red-200 border-red-800/30",
  fire_control: "bg-orange-600/20 text-orange-200 border-orange-500/30",
  rescue_operative: "bg-amber-600/20 text-amber-200 border-amber-500/30",
  search_technician: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  rescue_technician: "bg-cyan-600/20 text-cyan-300 border-cyan-600/30",
  sar_team_leader: "bg-cyan-700/20 text-cyan-200 border-cyan-600/30",
  search_manager: "bg-teal-600/20 text-teal-200 border-teal-500/30",
  rescue_manager: "bg-teal-700/20 text-teal-200 border-teal-600/30",
  sar_coordinator: "bg-teal-800/20 text-teal-200 border-teal-700/30",
  security_officer: "bg-yellow-600/20 text-yellow-300 border-yellow-500/30",
  security_supervisor: "bg-yellow-700/20 text-yellow-200 border-yellow-600/30",
  security_control: "bg-yellow-800/20 text-yellow-200 border-yellow-700/30",
  event_safety_officer: "bg-lime-600/20 text-lime-300 border-lime-500/30",
  close_protection: "bg-yellow-900/20 text-yellow-200 border-yellow-800/30",
  logistics_coordinator: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  fleet_support: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30",
  make_ready: "bg-zinc-600/20 text-zinc-300 border-zinc-600/30",
  welfare_officer: "bg-rose-400/20 text-rose-300 border-rose-400/30",
  communications_operator: "bg-blue-400/20 text-blue-300 border-blue-400/30",
  training_officer: "bg-indigo-400/20 text-indigo-300 border-indigo-400/30",
  hr: "bg-purple-400/20 text-purple-300 border-purple-400/30",
  finance: "bg-green-400/20 text-green-300 border-green-400/30",
  administrator: "bg-slate-400/20 text-slate-300 border-slate-400/30",
  manager: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  director: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
};

// Light badge colours for cards / modals
export const ROLE_BADGE_COLORS: Record<AppRole, string> = {
  first_responder: "bg-emerald-100 text-emerald-800 border-emerald-200",
  eca: "bg-teal-100 text-teal-800 border-teal-200",
  technician: "bg-green-100 text-green-800 border-green-200",
  nurse: "bg-pink-100 text-pink-800 border-pink-200",
  paramedic: "bg-blue-100 text-blue-800 border-blue-200",
  critical_care_paramedic: "bg-indigo-100 text-indigo-800 border-indigo-200",
  advanced_paramedic: "bg-violet-100 text-violet-800 border-violet-200",
  doctor: "bg-sky-100 text-sky-800 border-sky-200",
  consultant: "bg-rose-100 text-rose-800 border-rose-200",
  dispatcher: "bg-orange-100 text-orange-800 border-orange-200",
  incident_officer: "bg-amber-100 text-amber-800 border-amber-200",
  control_room_operator: "bg-orange-100 text-orange-800 border-orange-200",
  firefighter: "bg-red-100 text-red-800 border-red-200",
  leading_firefighter: "bg-red-100 text-red-800 border-red-300",
  crew_manager: "bg-red-200 text-red-900 border-red-400",
  watch_manager: "bg-red-200 text-red-900 border-red-400",
  station_manager: "bg-red-200 text-red-900 border-red-500",
  group_manager: "bg-red-300 text-red-900 border-red-500",
  fire_control: "bg-orange-200 text-orange-900 border-orange-400",
  rescue_operative: "bg-amber-100 text-amber-800 border-amber-300",
  search_technician: "bg-cyan-100 text-cyan-800 border-cyan-200",
  rescue_technician: "bg-cyan-100 text-cyan-800 border-cyan-300",
  sar_team_leader: "bg-cyan-200 text-cyan-900 border-cyan-400",
  search_manager: "bg-teal-100 text-teal-800 border-teal-300",
  rescue_manager: "bg-teal-200 text-teal-900 border-teal-400",
  sar_coordinator: "bg-teal-200 text-teal-900 border-teal-500",
  security_officer: "bg-yellow-100 text-yellow-800 border-yellow-200",
  security_supervisor: "bg-yellow-100 text-yellow-800 border-yellow-300",
  security_control: "bg-yellow-200 text-yellow-900 border-yellow-400",
  event_safety_officer: "bg-lime-100 text-lime-800 border-lime-200",
  close_protection: "bg-yellow-200 text-yellow-900 border-yellow-400",
  logistics_coordinator: "bg-slate-100 text-slate-700 border-slate-200",
  fleet_support: "bg-zinc-100 text-zinc-700 border-zinc-200",
  make_ready: "bg-zinc-100 text-zinc-700 border-zinc-300",
  welfare_officer: "bg-rose-100 text-rose-700 border-rose-200",
  communications_operator: "bg-blue-100 text-blue-700 border-blue-200",
  training_officer: "bg-indigo-100 text-indigo-700 border-indigo-200",
  hr: "bg-purple-100 text-purple-800 border-purple-200",
  finance: "bg-green-100 text-green-800 border-green-200",
  administrator: "bg-slate-100 text-slate-700 border-slate-300",
  manager: "bg-purple-100 text-purple-800 border-purple-200",
  director: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
};

// HCPC / registration body
export const REGISTRATION_BODY: Partial<Record<AppRole, string>> = {
  paramedic: "HCPC", critical_care_paramedic: "HCPC", advanced_paramedic: "HCPC",
  nurse: "NMC", doctor: "GMC", consultant: "GMC",
};
/** @deprecated Use REGISTRATION_BODY */
export const ROLE_REG_BODY = REGISTRATION_BODY;

// ─── Department labels ────────────────────────────────────────────────────────
export const DEPARTMENT_LABELS: Record<Department, string> = {
  operations: "Operations", clinical: "Clinical", fire_rescue: "Fire & Rescue",
  search_rescue: "Search & Rescue", security_operations: "Security Operations",
  fleet: "Fleet", logistics: "Logistics", governance: "Governance",
  technology: "Technology", customer_services: "Customer Services", corporate: "Corporate",
};

export const DEPARTMENT_COLORS: Record<Department, string> = {
  operations:          "bg-blue-100 text-blue-800 border-blue-200",
  clinical:            "bg-sky-100 text-sky-800 border-sky-200",
  fire_rescue:         "bg-red-100 text-red-800 border-red-200",
  search_rescue:       "bg-cyan-100 text-cyan-800 border-cyan-200",
  security_operations: "bg-yellow-100 text-yellow-800 border-yellow-200",
  fleet:               "bg-zinc-100 text-zinc-800 border-zinc-200",
  logistics:           "bg-slate-100 text-slate-700 border-slate-200",
  governance:          "bg-indigo-100 text-indigo-800 border-indigo-200",
  technology:          "bg-violet-100 text-violet-800 border-violet-200",
  customer_services:   "bg-teal-100 text-teal-800 border-teal-200",
  corporate:           "bg-purple-100 text-purple-800 border-purple-200",
};

// ─── Capability labels ────────────────────────────────────────────────────────
export const CAPABILITY_LABELS: Record<Capability, string> = {
  emergency_driver: "Emergency Driver", blue_light_driver: "Blue Light Driver",
  fleet_inspector: "Fleet Inspector", drug_competent: "Drug Competent",
  pgd_approved: "PGD Approved", prf_reviewer: "PRF Reviewer",
  bronze_commander: "Bronze Commander", silver_commander: "Silver Commander",
  gold_commander: "Gold Commander",
  rope_rescue: "Rope Rescue", water_rescue: "Water Rescue",
  swift_water: "Swift Water Rescue", confined_space: "Confined Space",
  ba_wearer: "BA Wearer", mountain_rescue: "Mountain Rescue",
  lowland_search: "Lowland Search", search_planning: "Search Planning",
  search_management: "Search Management", drone_operator: "Drone Operator",
  k9_handler: "K9 Handler", instructor: "Instructor", assessor: "Assessor",
  welfare_officer_cap: "Welfare Officer", gis_mapping: "GIS Mapping",
  rescue_boat_operator: "Rescue Boat Operator", technical_rescue: "Technical Rescue",
};

// ─── Authority level labels ───────────────────────────────────────────────────
export const AUTHORITY_LEVEL_LABELS: Record<AuthorityLevel, string> = {
  none: "None", team_leader: "Team Leader", supervisor: "Supervisor",
  duty_manager: "Duty Manager", bronze_commander: "Bronze Commander",
  silver_commander: "Silver Commander", gold_commander: "Gold Commander",
  executive: "Executive",
};

export const AUTHORITY_LEVEL_COLORS: Record<AuthorityLevel, string> = {
  none:             "bg-slate-100 text-slate-500 border-slate-200",
  team_leader:      "bg-green-100 text-green-800 border-green-200",
  supervisor:       "bg-blue-100 text-blue-800 border-blue-200",
  duty_manager:     "bg-indigo-100 text-indigo-800 border-indigo-200",
  bronze_commander: "bg-amber-100 text-amber-800 border-amber-200",
  silver_commander: "bg-slate-100 text-slate-700 border-slate-400",
  gold_commander:   "bg-yellow-100 text-yellow-800 border-yellow-400",
  executive:        "bg-purple-100 text-purple-800 border-purple-200",
};

// ─── Functional group labels ──────────────────────────────────────────────────
export const FUNCTIONAL_GROUP_LABELS: Record<FunctionalGroup, string> = {
  governance: "Governance", compliance: "Compliance", it_support: "IT Support",
  fleet_admin: "Fleet Admin", training_admin: "Training Admin",
  hr_admin: "HR Admin", finance_admin: "Finance", customer_services_admin: "Customer Services",
  radio_admin: "Radio Admin", platform_admin: "Platform Admin",
  gis_admin: "GIS Admin", communications_admin: "Communications Admin",
};

// ─── Demo users ───────────────────────────────────────────────────────────────
export interface DemoUser {
  id: string;
  name: string;
  initials: string;
  role: ProfessionalRole;
  /** Which service / division the user belongs to */
  department?: Department;
  /** Additive competencies that unlock additional modules */
  capabilities: Capability[];
  /** Operational command authority */
  authorityLevel: AuthorityLevel;
  /** Modular admin permission bundles */
  functionalGroups: FunctionalGroup[];
  /** Legacy operational role hats — kept for DB backward compat */
  operationalRoles: OperationalRole[];
  email: string;
  password: string;
  avatarColor: string;
  description: string;
}

export const DEMO_USERS: DemoUser[] = [
  {
    id: "u1", name: "Thomas Clarke", initials: "TC", role: "director",
    department: "operations",
    capabilities: [], authorityLevel: "executive",
    functionalGroups: ["governance", "compliance", "hr_admin", "fleet_admin", "training_admin", "finance_admin", "platform_admin"],
    operationalRoles: ["director", "management"],
    email: "t.clarke@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-purple-500 to-purple-700",
    description: "Director — full access across all modules",
  },
  {
    id: "u2", name: "James Harrison", initials: "JH", role: "paramedic",
    department: "clinical",
    capabilities: ["emergency_driver", "drug_competent"], authorityLevel: "none",
    functionalGroups: [], operationalRoles: [],
    email: "j.harrison@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-blue-500 to-blue-700",
    description: "Paramedic — clinical access, shifts, governance docs",
  },
  {
    id: "u3", name: "Sarah Mitchell", initials: "SM", role: "eca",
    department: "operations",
    capabilities: [], authorityLevel: "none",
    functionalGroups: [], operationalRoles: ["make_ready_crew"],
    email: "s.mitchell@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-teal-500 to-teal-700",
    description: "ECA + Make Ready — reports, shifts, policies",
  },
  {
    id: "u4", name: "Daniel Okafor", initials: "DO", role: "critical_care_paramedic",
    department: "clinical",
    capabilities: ["prf_reviewer"], authorityLevel: "supervisor",
    functionalGroups: ["governance"], operationalRoles: ["governance"],
    email: "d.okafor@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-indigo-500 to-indigo-700",
    description: "CCP + Governance Lead — clinical governance, PRF review, stats",
  },
  {
    id: "u5", name: "Amelia Foster", initials: "AF", role: "first_responder",
    department: "fire_rescue",
    capabilities: ["ba_wearer"], authorityLevel: "none",
    functionalGroups: [], operationalRoles: ["fire_crew"],
    email: "a.foster@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-emerald-500 to-emerald-700",
    description: "First Responder + Fire Crew — medical & fire shifts",
  },
  {
    id: "u6", name: "Priya Sharma", initials: "PS", role: "paramedic",
    department: "fire_rescue",
    capabilities: ["prf_reviewer"], authorityLevel: "supervisor",
    functionalGroups: ["compliance"], operationalRoles: ["fire_oic", "compliance"],
    email: "p.sharma@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-red-500 to-red-700",
    description: "Paramedic + Fire OIC + Compliance — dual medical/fire, CQC access",
  },
  {
    id: "u7", name: "Emma Clarke", initials: "EC", role: "paramedic",
    department: "clinical",
    capabilities: [], authorityLevel: "none",
    functionalGroups: [], operationalRoles: [],
    email: "e.clarke@ockhamrescue.co.uk", password: "demo1234",
    avatarColor: "from-sky-500 to-sky-700",
    description: "Paramedic — clinical access, shifts",
  },
];

// ─── Per-tenant user lists ────────────────────────────────────────────────────
// TENANT_USERS["demo"]   → generic Demo* accounts (password: OpscomDemo26!)
// TENANT_USERS["ockham"] → real Ockham Rescue staff (no demo shortcuts shown)
// Passwords are intentionally plain-text here because there is no real backend;
// replace with hashed + server-side auth before going to production.

// ─── Role slot compatibility ──────────────────────────────────────────────────
// Maps a SLOT role (what the event requires) to the set of AppRoles that are
// considered qualified to fill it. A Paramedic slot may not be filled by an ECA,
// but a Paramedic slot CAN be filled by an AP or CCP (upward-compatible).
// Used when assigning staff to event slots and when validating applications.
export const SLOT_COMPATIBLE_ROLES: Record<AppRole, AppRole[]> = {
  // First Responder slot → FR only
  first_responder: ["first_responder"],
  // ECA slot → ECA or any higher clinical
  eca: ["eca", "technician", "paramedic", "advanced_paramedic", "critical_care_paramedic", "nurse", "doctor", "consultant"],
  // Technician slot → Technician or higher
  technician: ["technician", "paramedic", "advanced_paramedic", "critical_care_paramedic", "nurse", "doctor", "consultant"],
  // Nurse slot → Nurse, Doctor, Consultant
  nurse: ["nurse", "doctor", "consultant"],
  // Paramedic slot → Paramedic or higher
  paramedic: ["paramedic", "advanced_paramedic", "critical_care_paramedic", "doctor", "consultant"],
  // AP slot → AP, CCP, Doctor, Consultant
  advanced_paramedic: ["advanced_paramedic", "critical_care_paramedic", "doctor", "consultant"],
  // CCP slot → CCP, Doctor, Consultant only
  critical_care_paramedic: ["critical_care_paramedic", "doctor", "consultant"],
  // Doctor slot → Doctor or Consultant
  doctor: ["doctor", "consultant"],
  // Consultant → Consultant only
  consultant: ["consultant"],
  // Non-clinical roles — exact match required
  dispatcher: ["dispatcher"],
  incident_officer: ["incident_officer", "manager", "director"],
  control_room_operator: ["control_room_operator", "dispatcher"],
  welfare_officer: ["welfare_officer"],
  communications_operator: ["communications_operator"],
  training_officer: ["training_officer"],
  manager: ["manager", "director"],
  director: ["director"],
  // Fire roles
  firefighter: ["firefighter", "leading_firefighter", "crew_manager", "watch_manager", "station_manager", "group_manager"],
  leading_firefighter: ["leading_firefighter", "crew_manager", "watch_manager", "station_manager", "group_manager"],
  crew_manager: ["crew_manager", "watch_manager", "station_manager", "group_manager"],
  watch_manager: ["watch_manager", "station_manager", "group_manager"],
  station_manager: ["station_manager", "group_manager"],
  group_manager: ["group_manager"],
  fire_control: ["fire_control"],
  rescue_operative: ["rescue_operative", "rescue_technician"],
  // SAR roles
  search_technician: ["search_technician", "rescue_technician", "sar_team_leader"],
  rescue_technician: ["rescue_technician", "sar_team_leader"],
  sar_team_leader: ["sar_team_leader"],
  search_manager: ["search_manager"],
  rescue_manager: ["rescue_manager"],
  sar_coordinator: ["sar_coordinator"],
  // Security
  security_officer: ["security_officer", "security_supervisor"],
  security_supervisor: ["security_supervisor"],
  security_control: ["security_control"],
  event_safety_officer: ["event_safety_officer"],
  close_protection: ["close_protection"],
  // Logistics / Admin
  logistics_coordinator: ["logistics_coordinator"],
  fleet_support: ["fleet_support"],
  make_ready: ["make_ready"],
  hr: ["hr", "manager", "director"],
  finance: ["finance", "manager", "director"],
  administrator: ["administrator", "manager", "director"],
};

export const TENANT_USERS: Record<string, DemoUser[]> = {
  // ── demo.opscom.co.uk ──────────────────────────────────────────────────────
  demo: [
    {
      id: "d1", name: "Demo Director", initials: "DD", role: "director",
      department: "operations",
      capabilities: [], authorityLevel: "executive",
      functionalGroups: ["governance", "compliance", "hr_admin", "fleet_admin", "training_admin", "finance_admin", "platform_admin"],
      operationalRoles: ["director", "management"],
      email: "director@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-purple-500 to-purple-700",
      description: "Director — full access across all modules",
    },
    {
      id: "d2", name: "Demo Manager", initials: "DM", role: "manager",
      department: "operations",
      capabilities: [], authorityLevel: "duty_manager",
      functionalGroups: ["hr_admin", "fleet_admin"],
      operationalRoles: ["management"],
      email: "manager@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-violet-500 to-violet-700",
      description: "Operations Manager — staff, scheduling, fleet, customers",
    },
    {
      id: "d3", name: "Demo Paramedic", initials: "DP", role: "paramedic",
      department: "clinical",
      capabilities: ["emergency_driver", "drug_competent"], authorityLevel: "none",
      functionalGroups: [], operationalRoles: [],
      email: "paramedic@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-blue-500 to-blue-700",
      description: "Paramedic — clinical access, PRFs, shifts",
    },
    {
      id: "d4", name: "Demo Dispatcher", initials: "DS", role: "dispatcher",
      department: "operations",
      capabilities: [], authorityLevel: "none",
      functionalGroups: [], operationalRoles: [],
      email: "dispatcher@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-orange-500 to-orange-700",
      description: "Dispatcher — CAD, crew status, briefings",
    },
    {
      id: "d5", name: "Demo Governance", initials: "DG", role: "critical_care_paramedic",
      department: "clinical",
      capabilities: ["prf_reviewer"], authorityLevel: "supervisor",
      functionalGroups: ["governance"], operationalRoles: ["governance"],
      email: "governance@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-indigo-500 to-indigo-700",
      description: "CCP + Governance Lead — PRF review, incidents, risk register",
    },
    {
      id: "d6", name: "Demo Firefighter", initials: "DF", role: "firefighter",
      department: "fire_rescue",
      capabilities: ["ba_wearer", "emergency_driver"], authorityLevel: "none",
      functionalGroups: [], operationalRoles: ["fire_crew"],
      email: "firefighter@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-red-500 to-red-700",
      description: "Firefighter — fire & rescue operations, appliance, shifts",
    },
    {
      id: "d7", name: "Demo SAR", initials: "DS", role: "sar_team_leader",
      department: "search_rescue",
      capabilities: ["search_planning", "rope_rescue", "lowland_search"], authorityLevel: "team_leader",
      functionalGroups: [], operationalRoles: [],
      email: "sar@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-cyan-500 to-cyan-700",
      description: "SAR Team Leader — search planning, sector allocation, rescue",
    },
    {
      id: "d8", name: "Demo Security", initials: "DS", role: "security_supervisor",
      department: "security_operations",
      capabilities: [], authorityLevel: "supervisor",
      functionalGroups: [], operationalRoles: [],
      email: "security@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-yellow-500 to-yellow-700",
      description: "Security Supervisor — patrols, incidents, access control",
    },
    {
      id: "d9", name: "Demo User", initials: "DU", role: "eca",
      department: "clinical",
      capabilities: [], authorityLevel: "none",
      functionalGroups: [], operationalRoles: [],
      email: "user@opscom.demo", password: "OpscomDemo26!",
      avatarColor: "from-teal-500 to-teal-700",
      description: "ECA — reports, shifts, policies (limited access)",
    },
  ],

  // ── ockham.opscom.co.uk ───────────────────────────────────────────────────
  ockham: [
    {
      id: "u1", name: "James Heritage", initials: "JH", role: "director",
      department: "operations",
      capabilities: [], authorityLevel: "executive",
      functionalGroups: ["governance", "compliance", "hr_admin", "fleet_admin", "training_admin", "finance_admin", "platform_admin"],
      operationalRoles: ["director", "management", "governance", "compliance"],
      email: "j.heritage92@hotmail.com", password: "Ockham@2026",
      avatarColor: "from-purple-500 to-purple-700",
      description: "Director — full access across all modules",
    },
    {
      id: "u2", name: "Sarah Mitchell", initials: "SM", role: "manager",
      department: "operations",
      capabilities: [], authorityLevel: "duty_manager",
      functionalGroups: ["hr_admin"],
      operationalRoles: ["management"],
      email: "s.mitchell@ockhamrescue.co.uk", password: "Ockham@2026",
      avatarColor: "from-violet-500 to-violet-700",
      description: "Operations Manager — staff, scheduling, fleet, customers",
    },
    {
      id: "u3", name: "Tom Williams", initials: "TW", role: "paramedic",
      department: "fire_rescue",
      capabilities: ["emergency_driver"], authorityLevel: "supervisor",
      functionalGroups: [],
      operationalRoles: ["fire_oic"],
      email: "t.williams@ockhamrescue.co.uk", password: "Ockham@2026",
      avatarColor: "from-red-500 to-red-700",
      description: "Paramedic + Fire OIC — dual medical/fire role",
    },
    {
      id: "u4", name: "Rachel Hughes", initials: "RH", role: "paramedic",
      department: "clinical",
      capabilities: ["prf_reviewer"], authorityLevel: "supervisor",
      functionalGroups: ["governance"],
      operationalRoles: ["governance"],
      email: "r.hughes@ockhamrescue.co.uk", password: "Ockham@2026",
      avatarColor: "from-indigo-500 to-indigo-700",
      description: "Paramedic + Governance Lead — PRF review, incidents, risk register",
    },
    {
      id: "u5", name: "Chris Ford", initials: "CF", role: "dispatcher",
      department: "operations",
      capabilities: [], authorityLevel: "none",
      functionalGroups: [], operationalRoles: [],
      email: "c.ford@ockhamrescue.co.uk", password: "Ockham@2026",
      avatarColor: "from-orange-500 to-orange-700",
      description: "Dispatcher — CAD, crew status, briefings",
    },
    {
      id: "u6", name: "Demo User", initials: "DU", role: "eca",
      department: "clinical",
      capabilities: [], authorityLevel: "none",
      functionalGroups: [], operationalRoles: [],
      email: "demo@ockhamrescue.co.uk", password: "OpscomDemo26!",
      avatarColor: "from-blue-500 to-blue-700",
      description: "ECA — limited access for demonstration",
    },
  ],
};
